import { ArrecadacaoItemResponse } from "features/arrecadacoes/typings";
import useDeleteArrecadacao from "features/arrecadacoes/useDeleteArrecadacao";
import { showConfirm, showSuccess } from "infra/helpers/alerta";
import NewTable from "components/NewTable";
import { useLoading } from "components/GlobalLoading";
import HeaderTable from "./components/HeaderTable";
import useListagemArrecadacoes from "./components/useListagemArrecadacoesColumns";
import useDuplicarArrecadacao from "core/features/arrecadacoes/useDuplicarArrecadacao";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { MdEdit, MdPreview, MdDelete } from "react-icons/md";
import { HiOutlineDuplicate } from "react-icons/hi";

interface ArrecadacoesTableProps {
  data: ArrecadacaoItemResponse[];
  searchValue: string;
  onSearch: (q?: string) => any;
  selectedTipoBoleto?: boolean;
  onSelectTipoBoleto?: (type?: boolean) => any;
  onEdit: (data: ArrecadacaoItemResponse) => any;
  onOpenRateioModal?: ({ id, nome }: { id: string; nome: string }) => any;
  onCloseRateio?: (id: string) => any;
  onDeleted: () => any;
  isLoading?: boolean;
  ano: string;
  mes: string;
  refetch?: () => any;
  selectedItems: ArrecadacaoItemResponse[];
  setSelectedsItems: (ids: ArrecadacaoItemResponse[]) => any;
  onHistorico?: (id: string) => any;
}

function ArrecadacoesTable({
  data,
  searchValue,
  onSearch,
  selectedTipoBoleto,
  onSelectTipoBoleto,
  onEdit,
  onOpenRateioModal,
  onCloseRateio,
  onDeleted,
  isLoading,
  ano,
  mes,
  refetch,
  selectedItems,
  setSelectedsItems,
  onHistorico,
}: ArrecadacoesTableProps) {
  const columns = useListagemArrecadacoes({
    onOpenRateioModal,
    onCloseRateio,
  });

  const { mutate: deleteArrecadacao, isLoading: isLoadingDelete } =
    useDeleteArrecadacao();

  const { mutate: duplicarArrecadacao, isLoading: isLoadingDuplicar } =
    useDuplicarArrecadacao();

  useLoading(isLoadingDelete || isLoadingDuplicar);

  const onDelete = async (arrecadacao: ArrecadacaoItemResponse) => {
    const { isConfirmed, isDenied } = await showConfirm(
      "Deseja excluir a arrecadação? Ao excluir, irá remover todas as contas em aberto que foram geradas a partir dessa arrecadação!",
      {
        showDenyButton: true,
        denyButtonText: "Todas",
        confirmButtonText: "Deste mês",
      }
    );

    if (isConfirmed || isDenied) {
      const request = {
        id: arrecadacao.id,
        ano: Number(ano),
        mes: Number(mes),
        excluirTodas: isDenied ? 1 : 0,
      };

      deleteArrecadacao(request, {
        onSuccess: (data) => {
          showSuccess(data.data.message);
          onDeleted();
        },
      });
    }
  };

  const onDuplicar = async (arrecadacao: ArrecadacaoItemResponse) => {
    const { isConfirmed } = await showConfirm(
      "Uma nova arrecadação será criada com as mesmas configurações. Deseja continuar?",
      {
        denyButtonText: "Cancelar",
        confirmButtonText: "Sim",
      }
    );

    if (isConfirmed) {
      duplicarArrecadacao(arrecadacao.id, {
        onSuccess: (data) => {
          showSuccess("Arrecadação duplicada com sucesso.");
          refetch?.();
        },
      });
    }
  };

  return (
    <>
      <NewTable<ArrecadacaoItemResponse>
        title={() => (
          <HeaderTable
            value={searchValue}
            onSearch={onSearch}
            onSelectTipoBoleto={onSelectTipoBoleto}
            selectedTipoBoleto={selectedTipoBoleto}
            arrecadacoes={data}
            ano={ano}
            mes={mes}
            refetch={refetch}
            selectedArrecadacoes={selectedItems}
          />
        )}
        loading={isLoading}
        columns={columns}
        onRow={(v) => ({
          style: { cursor: "pointer" },
          onClick: () => {
            onEdit(v);
          },
        })}
        dataSource={data}
        customSelectedRows={{
          selecteds: selectedItems,
          handler: setSelectedsItems,
          actions: [],
        }}
        actions={[
          {
            title: "Editar",
            icon: () => <MdEdit />,
            permission: PermissaoEnum.Backoffice.Financeiro.Arrecadacao.Editar,
            onClick: (v) => {
              onEdit(v);
            },
          },
          {
            title: "Duplicar arrecadação",
            icon: () => <HiOutlineDuplicate />,
            permission: PermissaoEnum.Backoffice.Financeiro.Arrecadacao.Criar,
            onClick: (v) => {
              onDuplicar?.(v);
            },
          },
          {
            title: "Histórico",
            icon: () => <MdPreview />,
            onClick: (v) => {
              onHistorico?.(v.id);
            },
          },
          {
            title: "Excluir",
            icon: () => <MdDelete />,
            permission: PermissaoEnum.Backoffice.Financeiro.Arrecadacao.Excluir,
            onClick: (v) => {
              onDelete(v);
            },
          },
        ]}
      />
    </>
  );
}

export default ArrecadacoesTable;
